import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FolderKanban, Inbox, KeyRound, LogOut, Menu, Users, X } from 'lucide-react'
import { createContext, useContext, useState } from 'react'
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useToast } from '../components/toast'
import { Button, ErrorNote, Field, IconButton, Modal, Spinner, cx } from '../components/ui'
import { ApiError } from '../lib/api'
import type { User } from '../lib/types'
import { staffApi } from './api'

const UserContext = createContext<User | null>(null)

export function useCurrentUser(): User {
  const user = useContext(UserContext)
  if (!user) throw new Error('useCurrentUser outside dashboard')
  return user
}

const NAV = [
  { to: '/requests', label: 'Change Requests', icon: Inbox },
  { to: '/projects', label: 'Projects', icon: FolderKanban },
  { to: '/team', label: 'Team', icon: Users },
]

export default function DashboardLayout() {
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const queryClient = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: staffApi.me, retry: false, staleTime: 5 * 60_000 })
  const logout = useMutation({
    mutationFn: staffApi.logout,
    onSuccess: () => {
      queryClient.clear()
      window.location.assign('/login')
    },
  })

  if (me.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (me.error instanceof ApiError && me.error.status === 401) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  }
  if (!me.data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 text-sm text-ink-600">
        {(me.error as Error)?.message ?? 'Something went wrong.'}
        <Button onClick={() => me.refetch()}>Try again</Button>
      </div>
    )
  }
  const user = me.data

  const sidebar = (
    <nav className="flex h-full flex-col gap-1 p-3">
      <div className="mb-4 flex items-center gap-2.5 px-2 pt-1">
        <img src="/favicon.svg" alt="" className="size-8" />
        <div className="leading-tight">
          <p className="text-[15px] font-semibold text-ink-950">Website Reviewr</p>
          <p className="text-[11px] text-ink-500">Computer CoPilot</p>
        </div>
      </div>
      {NAV.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={() => setMenuOpen(false)}
          className={({ isActive }) =>
            cx(
              'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-surface text-ink-950 shadow-card ring-1 ring-ink-200' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-950',
            )
          }
        >
          <Icon className="size-4.5" />
          {label}
        </NavLink>
      ))}
      <div className="mt-auto border-t border-ink-200 pt-3">
        <div className="px-2.5 pb-2">
          <p className="truncate text-sm font-medium text-ink-900">{user.name}</p>
          <p className="truncate text-xs text-ink-500">{user.email}</p>
        </div>
        <button
          type="button"
          onClick={() => setPasswordOpen(true)}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-ink-600 hover:bg-ink-100 hover:text-ink-950"
        >
          <KeyRound className="size-4" /> Change password
        </button>
        <button
          type="button"
          onClick={() => logout.mutate()}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-ink-600 hover:bg-ink-100 hover:text-ink-950"
        >
          <LogOut className="size-4" /> Sign out
        </button>
      </div>
    </nav>
  )

  return (
    <UserContext.Provider value={user}>
      <div className="min-h-dvh lg:pl-60">
        <aside className="fixed inset-y-0 left-0 hidden w-60 border-r border-ink-200 bg-paper lg:block">{sidebar}</aside>
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-ink-200 bg-paper/90 px-3 backdrop-blur lg:hidden">
          <IconButton label="Open menu" onClick={() => setMenuOpen(true)}>
            <Menu className="size-5" />
          </IconButton>
          <img src="/favicon.svg" alt="" className="size-6" />
          <span className="font-semibold">Website Reviewr</span>
        </header>
        {menuOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-ink-950/30" onClick={() => setMenuOpen(false)} />
            <aside className="absolute inset-y-0 left-0 w-64 animate-rise bg-paper shadow-pop">
              <IconButton label="Close menu" className="absolute top-3 right-3" onClick={() => setMenuOpen(false)}>
                <X className="size-4.5" />
              </IconButton>
              {sidebar}
            </aside>
          </div>
        )}
        <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-8 sm:py-8">
          <Outlet />
        </main>
      </div>
      <PasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </UserContext.Provider>
  )
}

function PasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const change = useMutation({
    mutationFn: () => staffApi.changePassword(current, next),
    onSuccess: () => {
      toast.success('Password updated. Other sessions were signed out.')
      setCurrent('')
      setNext('')
      onClose()
    },
  })
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Change password"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={change.isPending} onClick={() => change.mutate()} disabled={next.length < 10}>
            Update password
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Current password" htmlFor="pw-current">
          <input id="pw-current" type="password" className="field" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </Field>
        <Field label="New password" htmlFor="pw-new" hint="At least 10 characters.">
          <input id="pw-new" type="password" className="field" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        </Field>
        <ErrorNote>{change.error?.message}</ErrorNote>
      </div>
    </Modal>
  )
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-[-0.01em] text-ink-950">{title}</h1>
        {description && <p className="mt-1 text-sm text-ink-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  )
}
